import React from 'react';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import {
  addEpdFile,
  useArrangeSystemBoundaryFilesMutation,
  useAddSystemBoundaryFileMutation,
  useRemoveSystemBoundaryFileMutation,
  useUpdateSystemBoundaryMutation,
} from 'services/api/mutations';
import { useDictionaryValues, useEpdSystemBoundaryData } from 'services/api/queries';
import { FieldPanelFullWidth, HelpBoxContainer } from 'styles/v2/Styles.styled';

import CompilerSelectControlledComponent from '../epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextAreaComponent from '../epd-wizard-inputs/WizardTextArea';
import { ImagesUpload } from '../images-upload/ImagesUpload';
import SmartLabel from '../labels/SmartLabel';
import SystemBoundaryGrid from './grids/system-boundary/SystemBoundaryGrid';
import GridPanel from './panels/expandable/GridPanel';
import usePcrV2, { usePcrV2Loading } from './hooks/usePcrV2';
import SkeletonSystemBoundaryTab from './skeletons/SkeletonSystemBoundaryTab';
import { HelpBox } from '../help-boxes';

const SystemBoundaryTab: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  validationState: any;
}> = ({ epdId, epdVersionId, validationState }) => {
  const dictionariesQuery = useDictionaryValues();
  const boundaryData = useEpdSystemBoundaryData(epdVersionId).data;
  const updateSystemBoundaryMutation = useUpdateSystemBoundaryMutation(epdVersionId);
  const isPcrV2Loading = usePcrV2Loading();
  const isPcrV2 = usePcrV2();

  const addSystemBoundaryFileMutation = useAddSystemBoundaryFileMutation(epdVersionId);
  const removeSystemBoundaryFileMutation = useRemoveSystemBoundaryFileMutation(epdVersionId);
  const arrangeSystemBoundaryFilesMutation = useArrangeSystemBoundaryFilesMutation(epdVersionId);

  const isLoading =
    dictionariesQuery.data === undefined || boundaryData === undefined || isPcrV2Loading || validationState === undefined;
  if (isLoading) {
    return <SkeletonSystemBoundaryTab />;
  }

  const systemBoundaryDescriptionLookup = dictionariesQuery.data.filter(
    isPcrV2
      ? (x) => x.dictionaryCode === EPD_DICTIONARY_CODES.SYSTEM_BOUNDARY_DESCRIPTION_V2
      : (x) => x.dictionaryCode === EPD_DICTIONARY_CODES.SYSTEM_BOUNDARY_DESCRIPTION
  );

  const systemBoundaryDescriptionOptions =
    systemBoundaryDescriptionLookup.map((x) => {
      return {
        value: x.key,
        label: x.value,
      };
    }) || [];

  const systemBoundaryHasExcludedModulesLookup = [
    { value: true, label: 'Yes, there is an excluded module, or there are excluded modules' },
    { value: false, label: 'No, there is no excluded module, or there are no excluded modules' },
  ];

  const errors = validationState.errors;

  const onAddSystemBoundaryFile = async (propertyName: string, file: File) => {
    await addEpdFile(
      { epdId, propertyName, file, documentTypeId: null, documentLanguageId: null },
      addSystemBoundaryFileMutation
    );
  };

  const onRemoveSystemBoundaryFile = async (fileId: string, propertyName: string) => {
    removeSystemBoundaryFileMutation.mutate(fileId);
  };

  const onChangeEpdSystemBoundaryData = async (propertyName: string, val: any) => {
    updateSystemBoundaryMutation.mutate({
      epdId,
      versionId: epdVersionId,
      propertyName: propertyName,
      newValue: val,
    });
  };

  return (
    <FieldPanelFullWidth>
      <CompilerSelectControlledComponent
        label="Description of the system boundary"
        tooltip="Description of the EPD system boundary as one of the options (a-f) in PCR 2019:14"
        name="systemBoundaryDescription"
        options={systemBoundaryDescriptionOptions}
        value={systemBoundaryDescriptionOptions.find((x) => x.value == boundaryData?.description)}
        onChanged={onChangeEpdSystemBoundaryData}
        placeholder="Select"
        isClearable={false}
        error={errors.systemBoundaryDescription}
        required
      />

      <CompilerSelectControlledComponent
        label="Excluded modules"
        tooltip="Information on which life-cycle stages are not considered (if any), with a justification of the omission."
        name="hasExcludedModules"
        options={systemBoundaryHasExcludedModulesLookup}
        value={systemBoundaryHasExcludedModulesLookup.find((x) => x.value == boundaryData?.hasExcludedModules)}
        onChanged={onChangeEpdSystemBoundaryData}
        placeholder="Select"
        isClearable={false}
        error={errors.hasExcludedModules}
        required
      />
      {boundaryData?.hasExcludedModules && (
        <WizardTextAreaComponent
          label="Justification for the omission of modules"
          tooltip="Information on which life-cycle stages are not considered (if any), with a justification of the omission."
          value={boundaryData?.excludedModulesJustification}
          onChanged={onChangeEpdSystemBoundaryData}
          name="excludedModulesJustification"
          error={errors.excludedModulesJustification}
          required
        />
      )}
      <GridPanel
        title="Declared modules"
        error={errors.declaredModulesErrors && Object.keys(errors.declaredModulesErrors).length > 0 && 'Required'}
      >
        <SystemBoundaryGrid
          epdId={epdId}
          epdVersionId={epdVersionId}
          errors={{
            declaredModules: errors.declaredModulesErrors?.declaredModules,
            declaredModulesGeography: errors.declaredModulesErrors?.declaredModulesGeography,
            systemBoundary: errors.declaredModulesErrors?.systemBoundary,
          }}
        />
      </GridPanel>
      <WizardTextAreaComponent
        label="Description of the process flow diagram(s)"
        tooltip="Process flow diagram of the product system, divided into the life-cycle stages and modules, showing the main processes and the system boundary of the LCA. It shall clearly present when the end-of-waste state is reached for main input and output flows of reused/recycled materials and recovered energy."
        value={boundaryData?.processFlowDiagramsDescription}
        onChanged={onChangeEpdSystemBoundaryData}
        name="processFlowDiagramsDescription"
      />

      <div>
        <SmartLabel
          label={'Process flow diagram(s) related images'}
          required={isPcrV2}
          hasValue={boundaryData?.processFlowDiagramsImages && boundaryData?.processFlowDiagramsImages?.length > 0}
          error={errors?.processFlowDiagramsImages}
        />
        <ImagesUpload
          name={'ProcessFlowDiagramsImages'}
          images={boundaryData?.processFlowDiagramsImages}
          onUpload={onAddSystemBoundaryFile}
          onReorder={(imgIds: string[]) => arrangeSystemBoundaryFilesMutation.mutate(imgIds)}
          onRemove={onRemoveSystemBoundaryFile}
        />
        <HelpBoxContainer>
          {boundaryData?.processFlowDiagramsImages && boundaryData?.processFlowDiagramsImages.length > 1 && (
            <HelpBox>
              <span>Use drag and drop to reorder the images.</span>
            </HelpBox>
          )}
          <HelpBox>
            <span>
              Upload an image with a recommended resolution of 150–300 DPI. Images will be proportionally adjusted to fit the
              page size. To see image placement in the document, use the "PDF preview" option in the upper right corner.
            </span>
          </HelpBox>
        </HelpBoxContainer>
      </div>
    </FieldPanelFullWidth>
  );
};

export default SystemBoundaryTab;
